import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun } from 'lucide-react';
import { IDIOMAS, trocarIdioma, type Idioma } from '../../i18n';
import { api, type Eu } from '../api';
import i18n from 'i18next';

/** Guarda a preferência no usuário quando há sessão (no login, só no navegador). */
function salvarNoServidor(p: { idioma?: Idioma; tema?: 'escuro' | 'claro' }) {
  void api('/api/v1/auth/preferencias', { method: 'PATCH', json: p }).catch(() => undefined);
}

export function SeletorIdioma() {
  const { t, i18n } = useTranslation();
  return (
    <div role="group" aria-label={t('shell.idioma')} className="flex rounded-lg border border-borda p-0.5 font-mono text-xs">
      {IDIOMAS.map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => {
            trocarIdioma(i as Idioma);
            salvarNoServidor({ idioma: i as Idioma });
          }}
          aria-pressed={i18n.language === i}
          className={`rounded-md px-2 py-1 uppercase ${i18n.language === i ? 'bg-sup-3 text-tinta-1' : 'text-tinta-3 hover:text-tinta-1'}`}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

export type Tema = 'escuro' | 'claro';
export type OrigemTema = 'escolha-local' | 'usuario' | 'workspace' | 'sistema' | 'padrao';

/** Escolha feita no alternador, guardada com a identidade de quem escolheu (não vale para outro usuário). */
const CHAVE_ESCOLHA = 'erp.tema.escolha';

/** Tema efetivo no <html>. */
export function temaAtual(): Tema {
  return document.documentElement.dataset.tema === 'claro' ? 'claro' : 'escuro';
}

let ultimaOrigem: OrigemTema | null = null;
let ouvindoSistema = false;
const ouvintesTema = new Set<() => void>();

/** Avisa quando o tema efetivo muda (escolha, aparência ou sistema), para o alternador acompanhar sem remontar. */
export function assinarTema(cb: () => void): () => void {
  ouvintesTema.add(cb);
  return () => void ouvintesTema.delete(cb);
}

/** Único ponto que escreve o tema no <html>: guarda a origem junto, para o evento do sistema saber se pode mexer. */
function definirTema(tema: Tema, origem: OrigemTema): void {
  document.documentElement.dataset.tema = tema;
  ultimaOrigem = origem;
  for (const cb of ouvintesTema) cb();
}

function temaValido(t: unknown): t is Tema {
  return t === 'escuro' || t === 'claro';
}

/** Tema do sistema operacional, ou null quando o navegador não informa. */
export function temaDoSistema(): Tema | null {
  try {
    if (typeof matchMedia !== 'function') return null;
    if (matchMedia('(prefers-color-scheme: light)').matches) return 'claro';
    if (matchMedia('(prefers-color-scheme: dark)').matches) return 'escuro';
  } catch {
    /* sem matchMedia */
  }
  return null;
}

/** Escolha local do alternador, só se foi feita pelo mesmo usuário. */
export function escolhaLocalDoUsuario(usuarioId: string): Tema | null {
  try {
    const bruto = localStorage.getItem(CHAVE_ESCOLHA);
    if (!bruto) return null;
    const v = JSON.parse(bruto) as { usuario_id?: unknown; tema?: unknown };
    return v.usuario_id === usuarioId && temaValido(v.tema) ? v.tema : null;
  } catch {
    return null;
  }
}

/**
 * DS0.1 (adjudicação G12 r4, P2-01): precedência do tema. Escolha explícita do usuário autenticado (a deste aparelho,
 * do mesmo usuário, ou a salva no servidor, marcada por preferencias_definidas) → padrão válido do workspace →
 * prefers-color-scheme → escuro. A chave global legada erp.tema não entra: não identifica o usuário.
 */
export function resolverTema(e: { local: Tema | null; eu: Eu | null; sistema: Tema | null }): { tema: Tema; origem: OrigemTema } {
  if (e.local) return { tema: e.local, origem: 'escolha-local' };
  if (e.eu?.preferencias_definidas && temaValido(e.eu.usuario.tema)) return { tema: e.eu.usuario.tema, origem: 'usuario' };
  if (temaValido(e.eu?.aparencia?.tema)) return { tema: e.eu.aparencia.tema, origem: 'workspace' };
  if (e.sistema) return { tema: e.sistema, origem: 'sistema' };
  return { tema: 'escuro', origem: 'padrao' };
}

/** Escolha explícita no alternador: passa a valer sobre o sistema, inclusive em mudanças do sistema depois dela. */
export function aplicarTema(tema: Tema, usuarioId?: string): void {
  definirTema(tema, 'escolha-local');
  if (!usuarioId) return;
  try {
    localStorage.setItem(CHAVE_ESCOLHA, JSON.stringify({ usuario_id: usuarioId, tema }));
  } catch {
    /* sem storage */
  }
}

export function AlternarTema({ usuarioId }: { usuarioId?: string }) {
  const { t } = useTranslation();
  const tema = useSyncExternalStore(assinarTema, temaAtual, () => 'escuro' as Tema);
  const proximo = tema === 'escuro' ? 'claro' : 'escuro';
  return (
    <button
      type="button"
      onClick={() => {
        aplicarTema(proximo, usuarioId);
        salvarNoServidor({ tema: proximo });
      }}
      title={`${t('shell.tema')}: ${t(proximo === 'claro' ? 'shell.temaClaro' : 'shell.temaEscuro')}`}
      aria-label={t('shell.tema')}
      className="grid size-8 place-items-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1"
    >
      {tema === 'escuro' ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
    </button>
  );
}

function escolhaLocal(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

/** Variáveis que o fluxo antigo de Aparência escrevia inline no <html> (aparencia.cor). */
export const OVERRIDES_ACENTO = ['--acento', '--acento-forte', '--acento-suave', '--acento-tinta', '--acento-texto'] as const;

/** Mudança do tema do sistema só é acompanhada quando não há escolha pessoal nem padrão do workspace. */
function acompanharSistema(): void {
  if (ouvindoSistema || typeof matchMedia !== 'function') return;
  ouvindoSistema = true;
  try {
    matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (ultimaOrigem !== 'sistema' && ultimaOrigem !== 'padrao') return;
      const r = resolverTema({ local: null, eu: null, sistema: temaDoSistema() });
      definirTema(r.tema, r.origem);
    });
  } catch {
    /* navegador sem evento de mudança */
  }
}

/**
 * M13.1 e DS0.1: aplica a aparência do workspace. O tom de acento ficou congelado na cor da marca (decisão de Allan,
 * 19/10): aparencia.cor não tem mais efeito visual. O valor gravado não é apagado nem reinterpretado; aqui só se
 * removem os overrides inline que o fluxo antigo deixava no <html>, para nenhum vencer os tokens. Tema segue
 * resolverTema; idioma do workspace vale para quem ainda não escolheu.
 */
export function aplicarAparencia(eu: Eu): Tema {
  const raiz = document.documentElement.style;
  for (const v of OVERRIDES_ACENTO) raiz.removeProperty(v);
  const r = resolverTema({ local: escolhaLocalDoUsuario(eu.usuario.id), eu, sistema: temaDoSistema() });
  definirTema(r.tema, r.origem);
  acompanharSistema();
  const doUsuario = !!eu.preferencias_definidas;
  if (!escolhaLocal('erp.idioma')) {
    const idioma = doUsuario ? eu.usuario.idioma : (eu.aparencia?.idioma ?? eu.usuario.idioma);
    if (i18n.language !== idioma) void i18n.changeLanguage(idioma);
    document.documentElement.lang = idioma === 'pt' ? 'pt-BR' : idioma;
  }
  return r.tema;
}
